// src/components/layout/RightPanel/MermaidExtension.jsx
import { LanguageSupport, StreamLanguage } from '@codemirror/language';
import { tags as t } from '@codemirror/highlight';
import { EditorView, Decoration, ViewPlugin, WidgetType } from '@codemirror/view';

/* ========================
 * createMermaidExtension - Mermaid diagram support with live preview
 * Provides syntax highlighting and async diagram rendering
 * ======================== */

// Mermaid-specific theme with fixed selectors
const mermaidTheme = EditorView.theme({
    '&[data-content-type="mermaid"]': {
        backgroundColor: 'var(--background)'
    },
    '&[data-content-type="mermaid"] .cm-content': {
        fontFamily: 'var(--code-font-family)',
        padding: '0.75rem 0.5rem',
        lineHeight: '1.5'
    },
    '&[data-content-type="mermaid"] .cm-line': {
        padding: '0 0.5rem',
        lineHeight: '1.5'
    },
    // Line number gutter styling for mermaid mode
    '&[data-content-type="mermaid"] .cm-gutters': {
        backgroundColor: 'var(--background-dark)',
        borderRight: '1px solid var(--border-color)',
        color: 'var(--on-surface-dim)',
        paddingLeft: '0.5rem',
        paddingRight: '0.5rem'
    },
    '&[data-content-type="mermaid"] .cm-lineNumbers': {
        minWidth: '2rem'
    },
    '&[data-content-type="mermaid"] .cm-lineNumbers .cm-gutterElement': {
        padding: '0 0.25rem',
        minWidth: '1.5em',
        textAlign: 'right',
        lineHeight: '1.5'
    },
    '.mermaid-preview-widget': {
        margin: '1rem 0',
        padding: '1rem',
        backgroundColor: 'var(--background-dark)',
        border: '1px solid var(--border-color)',
        borderRadius: '8px',
        textAlign: 'center',
        minHeight: '60px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--on-surface-dim)',
        fontSize: '14px'
    }
});

// Simple Mermaid syntax highlighting
const mermaidLanguage = StreamLanguage.define({
    name: 'mermaid',
    startState: () => ({ inDirective: false }),
    
    token(stream, state) {
        // Handle directives
        if (stream.match(/^%%\{[^}]*\}%%/)) {
            return t.meta;
        }
        
        // Handle comments
        if (stream.match(/^%%.*$/)) {
            return t.comment;
        }
        
        // Handle keywords
        if (stream.match(/^(graph|flowchart|sequenceDiagram|classDiagram|gitgraph|erDiagram|journey|pie|gantt|quadrantChart|requirement|mindmap|timeline|zenuml|sankey)/)) {
            return t.keyword;
        }
        
        // Handle direction keywords
        if (stream.match(/^(TD|TB|BT|RL|LR)/)) {
            return t.keyword;
        }
        
        // Handle arrows and connections
        if (stream.match(/^(-->|---|-\.\->|===>|==>|\|)/)) {
            return t.operator;
        }
        
        // Handle node shapes
        if (stream.match(/^[\[\](){}]/)) {
            return t.bracket;
        }
        
        // Handle strings
        if (stream.match(/^"([^"\\]|\\.)*"/)) {
            return t.string;
        }
        
        // Handle labels
        if (stream.match(/^[|][^|]*[|]/)) {
            return t.string;
        }
        
        // Handle node IDs
        if (stream.match(/^[a-zA-Z][a-zA-Z0-9_]*/)) {
            return t.variableName;
        }
        
        stream.next();
        return null;
    }
});

// Mermaid preview widget - simplified without external mermaid dependency for now
class MermaidPreviewWidget extends WidgetType {
    constructor(source) {
        super();
        this.source = source;
    }

    toDOM() {
        const wrap = document.createElement('div');
        wrap.className = 'mermaid-preview-widget';

        // Simple preview for now - can be enhanced later with actual mermaid rendering
        wrap.innerHTML = `
            <div>
                <div style="margin-bottom: 8px; font-weight: 500;">📊 Mermaid Diagram</div>
                <div style="font-size: 12px; opacity: 0.7;">Live preview coming soon</div>
            </div>
        `;

        return wrap;
    }

    ignoreEvent() {
        return false;
    }
}

// Plugin to add preview widgets
const mermaidPreviewPlugin = ViewPlugin.fromClass(class {
    constructor(view) {
        this.decorations = this.buildDecorations(view);
    }

    update(update) {
        if (update.docChanged || update.viewportChanged) {
            this.decorations = this.buildDecorations(update.view);
        }
    }

    buildDecorations(view) {
        const widgets = [];
        const doc = view.state.doc;

        // Simple detection - look for mermaid blocks
        for (let lineNo = 1; lineNo <= doc.lines; lineNo++) {
            const line = doc.line(lineNo);
            const lineText = line.text.trim();

            // Check if this looks like a mermaid diagram start
            if (lineText.match(/^(graph|flowchart|sequenceDiagram|classDiagram|pie|gantt)/)) {
                // Find the end of the diagram (empty line or end of document)
                let endLine = lineNo;
                while (endLine < doc.lines) {
                    const nextLine = doc.line(endLine + 1);
                    if (!nextLine.text.trim()) break;
                    endLine++;
                }

                // Extract diagram source
                const startPos = line.from;
                const endPos = doc.line(endLine).to;
                const diagramSource = doc.sliceString(startPos, endPos);

                // Add widget after the diagram
                widgets.push(
                    Decoration.widget({
                        widget: new MermaidPreviewWidget(diagramSource),
                        side: 1
                    }).range(endPos)
                );

                // Skip processed lines
                lineNo = endLine;
            }
        }

        return Decoration.set(widgets);
    }
}, {
    decorations: v => v.decorations
});

export const createMermaidExtension = async () => {
    return [
        new LanguageSupport(mermaidLanguage),
        mermaidTheme,
        mermaidPreviewPlugin
    ];
};